import type {
  ActivityFilterOption,
  ActivityFilterType,
  AdditionalAttributeFilter,
  AdditionalFilterType,
  EnrichedData,
  LabeledActivityFilterOptions,
  PrimaryFilterType,
  SpecializationProgressLevel,
  UserFilter,
} from 'bundles/enterprise-admin-user-filters/types';

import _t from 'i18n!nls/enterprise-admin-user-filters';

export const DEFAULT_FOR_LAST_COMPLETION_WITHIN = 30;
export const DEFAULT_FOR_INACTIVITY = 7;

export const getPrimaryFilterOptions = (): { value: PrimaryFilterType; label: string; supportText: string }[] => [
  {
    label: _t('Invited but not joined programs'),
    supportText: _t('Identify learners that haven’t been joining programs'),
    value: 'INVITED',
  },
  {
    label: _t('Learners in a learning program'),
    supportText: _t('Identify learner progress, and inactivity in courses'),
    value: 'MEMBER',
  },
  {
    label: _t('Removed learners'),
    supportText: _t('Identify learners that have been removed from programs'),
    value: 'REMOVED_MEMBER',
  },
];

export const getPrimaryFilterLabel = (primaryFilter: PrimaryFilterType) =>
  getPrimaryFilterOptions().find((option) => option.value === primaryFilter)?.label;

export const getActivityFilterOptions = (): ActivityFilterOption[] => [
  {
    label: _t('No enrollments'),
    supportText: _t('Find the learners not engaging with any content'),
    value: 'NO_ENROLLMENTS',
  },
  {
    label: _t('General inactivity'),
    supportText: _t(
      'Find learners that have been recently inactive, regardless of whether they have previously enrolled in a course. '
    ),
    value: 'GENERAL_INACTIVITY',
  },
  {
    label: _t('Enrolled in a course / specialization'),
    supportText: _t('Find learners enrolled in specific courses or  specializations'),
    value: 'ENROLLED',
  },
  {
    label: _t('Enrolled but recently inactive'),
    supportText: _t('Find enrolled learners who have been recently inactive'),
    value: 'ENROLLED_BUT_INACTIVE',
  },
  {
    label: _t('Completed at least one course recently'),
    supportText: _t('Find learners that have completed at least one course'),
    value: 'COMPLETED',
  },
  {
    label: _t('Progress in specialization(s)'),
    supportText: _t('Find learners based on their progress across a multi-course specialization'),
    value: 'PROGRESS_IN_S12N',
  },
];

export const getActivityFilterLabel = (activityFilter: ActivityFilterType) =>
  getActivityFilterOptions().find((filter) => filter.value === activityFilter)?.label;

export const getLabeledActivityFilterOptions = (): LabeledActivityFilterOptions[] => {
  const activityFilterOptions = getActivityFilterOptions();
  return [
    {
      label: _t('Inactive learners'),
      options: [activityFilterOptions[0], activityFilterOptions[1]],
    },
    {
      label: _t('Enrolled learners'),
      options: [activityFilterOptions[2], activityFilterOptions[3]],
    },
    {
      label: _t('Progress across courses'),
      options: [activityFilterOptions[4]], // TODO: no support for progress in s12ns in the initial release  activityFilterOptions[5]],
    },
  ];
};

export const getSpecializationProgressLevels = (): { value: SpecializationProgressLevel; label: string }[] => [
  { value: 'ANY', label: _t('Any progress') },
  { value: 'NONE', label: _t('No progress (0% completed)') },
  { value: 'EARLY', label: _t('Early stage (up to 30% completed)') },
  { value: 'MID', label: _t('Mid stage (up to 70% completed)') },
  { value: 'LATE', label: _t('Late stage (more than 70% completed)') },
];

export type PresetUserFilterId = string;

export type PresetFilter = {
  filterId: PresetUserFilterId;
  filterName: string;
  programScopeName?: string;
  filter: UserFilter;
  enrichedData?: EnrichedData;
};

type PresetFiltersArguments = { programId?: string; programName?: string; contractId?: string; contractName?: string };

export const getPresetFilters = ({
  programId,
  programName,
  contractId,
  contractName,
}: PresetFiltersArguments = {}): PresetFilter[] => {
  const primaryFilter = programId ? { programIdSelection: { values: [programId] } } : undefined;
  const programSelected = programId && programName;
  const contractSelected = contractId && contractName;
  const enrichedData =
    programSelected || contractSelected
      ? {
          enrichedPrograms: programSelected ? [{ programId, programName }] : [],
          enrichedContracts: contractSelected ? [{ contractId, contractName }] : [],
          enrichedProducts: [],
        }
      : undefined;
  const additionalAttributeSelections: AdditionalAttributeFilter[] | undefined = contractSelected
    ? [
        {
          attributeName: 'CONTRACT',
          values: [contractId],
        },
      ]
    : undefined;
  return [
    {
      filterId: 'joined',
      filterName: _t('Joined a program'),
      programScopeName: _t('Joined this program'),
      filter: {
        primaryFilter: {
          type: 'MEMBER',
          ...primaryFilter,
        },
        additionalAttributeSelections,
      },
      enrichedData,
    },
    {
      filterId: 'invited',
      filterName: _t('Invited but never joined a program'),
      programScopeName: _t('Invited but never joined this program'),
      filter: {
        primaryFilter: {
          type: 'INVITED',
          ...primaryFilter,
        },
        additionalAttributeSelections,
      },
      enrichedData,
    },
    {
      filterId: 'joined-not-enrolled',
      filterName: _t('Joined a program but have no enrollments'),
      programScopeName: _t('Joined this program but have no enrollments'),
      filter: {
        primaryFilter: {
          type: 'MEMBER',
          activityFilter: {
            type: 'NO_ENROLLMENTS',
          },
          ...primaryFilter,
        },
        additionalAttributeSelections,
      },
      enrichedData,
    },
    {
      filterId: 'enrolled',
      filterName: _t('Enrolled'),
      filter: {
        primaryFilter: {
          type: 'MEMBER',
          activityFilter: {
            type: 'ENROLLED',
          },
          ...primaryFilter,
        },
        additionalAttributeSelections,
      },
      enrichedData,
    },
    {
      filterId: 'enrolled-inactive',
      filterName: _t('Enrolled but inactive for the past 30 days'),
      filter: {
        primaryFilter: {
          type: 'MEMBER',
          activityFilter: {
            type: 'ENROLLED_BUT_INACTIVE',
            lastActivityDate: { type: 'RELATIVE', nDaysAgoBefore: 30 },
          },
          ...primaryFilter,
        },
        additionalAttributeSelections,
      },
      enrichedData,
    },
    {
      filterId: 'inactive-90',
      filterName: _t('Inactive for the past 90 days'),
      filter: {
        primaryFilter: {
          type: 'MEMBER',
          activityFilter: {
            type: 'GENERAL_INACTIVITY',
            lastActivityDate: { type: 'RELATIVE', nDaysAgoBefore: 90 },
          },
          ...primaryFilter,
        },
        additionalAttributeSelections,
      },
      enrichedData,
    },
    {
      filterId: '1-completion-in-30-days',
      filterName: _t('At least 1 completion in the last 30 days'),
      filter: {
        primaryFilter: {
          type: 'MEMBER',
          activityFilter: {
            type: 'COMPLETED',
            completionDate: { type: 'RELATIVE', nDaysAgoAfter: 30 },
          },
          ...primaryFilter,
        },
        additionalAttributeSelections,
      },
      enrichedData,
    },
    {
      filterId: 'removed',
      filterName: _t('Removed from all programs'),
      programScopeName: _t('Removed from this program'),
      filter: {
        primaryFilter: {
          type: 'REMOVED_MEMBER',
          ...primaryFilter,
        },
        additionalAttributeSelections,
      },
      enrichedData,
    },
  ];
};

export const getDefaultUserFilterPreset = (args?: PresetFiltersArguments & { presetId?: string }) => {
  const presets = getPresetFilters(args);
  if (args?.presetId) {
    return presets.find((_) => _.filterId === args.presetId) ?? presets[0];
  }
  return presets[0];
};

export const getUserFilterPreset = (id: PresetUserFilterId) =>
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  getPresetFilters().find((preset) => preset.filterId === id)!;

export const getFilterName = (preset?: PresetFilter, program = false) => {
  if (!preset) return _t('Custom');
  if (!program) return preset.filterName;
  return preset.programScopeName ?? preset.filterName;
};

export const getAdditionalFilterOptions = (): {
  value: AdditionalFilterType;
  label: string;
  pluralLabel: string;
}[] => [
  {
    label: _t('Business unit'),
    pluralLabel: _t('Business units'),
    value: 'BUSINESS_UNIT',
  },
  {
    label: _t('Job type'),
    pluralLabel: _t('Job types'),
    value: 'JOB_TYPE',
  },
  {
    label: _t('Job title'),
    pluralLabel: _t('Job titles'),
    value: 'JOB_TITLE',
  },
  {
    label: _t('Location'),
    pluralLabel: _t('Locations'),
    value: 'LOCATION',
  },
  {
    label: _t('Manager'),
    pluralLabel: _t('Managers'),
    value: 'MANAGER',
  },
];

export const getAdditionalFilterLabel = (filter: AdditionalFilterType) =>
  [{ value: 'CONTRACT', pluralLabel: _t('Active contracts') }, ...getAdditionalFilterOptions()].find(
    (_) => _.value === filter
  )?.pluralLabel;
